/**
 * RemitSlice
 * 
 * Redux slice for managing remit (835 remittance advice) state.
 * 
 * This slice follows the modular pattern with:
 * - state.ts: State interface and initial state
 * - selectors.ts: Memoized selectors
 * - extraReducers.ts: API endpoint matchers
 * - reducers/: Split reducer files by domain
 */

import { createSlice } from '@reduxjs/toolkit';
import { initialState, RemitSliceState } from './state';
import { buildRemitExtraReducers } from './extraReducers';

// Import all reducers
import {
  setRemit,
  clearRemit,
  updateRemit,
  restoreFromBackup,
  setLoading,
  setSelectedClaimId,
  setSelectedServiceLineId,
  clearSelections,
  selectNextClaim,
  selectPreviousClaim,
  markFieldAsEdited,
  clearEditedFields,
  markAsSaved,
  discardChanges,
  setValidationError,
  setValidationErrors,
  clearValidationError,
  clearAllValidationErrors,
} from './reducers';

/**
 * Remit slice definition
 */
const remitSlice = createSlice({
  name: 'remit',
  initialState,
  reducers: {
    // Core reducers
    setRemit,
    clearRemit,
    updateRemit,
    restoreFromBackup,
    setLoading,

    // Selection reducers
    setSelectedClaimId,
    setSelectedServiceLineId,
    clearSelections,
    selectNextClaim,
    selectPreviousClaim,

    // Edit tracking reducers
    markFieldAsEdited,
    clearEditedFields,
    markAsSaved,
    discardChanges,

    // Validation reducers
    setValidationError,
    setValidationErrors,
    clearValidationError,
    clearAllValidationErrors,
  },
  extraReducers: buildRemitExtraReducers,
});

// Export actions
export const remitActions = remitSlice.actions;

// Export individual actions with Action suffix for convenience
export const {
  setRemit: setRemitAction,
  clearRemit: clearRemitAction,
  updateRemit: updateRemitAction,
  restoreFromBackup: restoreFromBackupAction,
  setLoading: setLoadingAction,
  setSelectedClaimId: setSelectedClaimIdAction,
  setSelectedServiceLineId: setSelectedServiceLineIdAction,
  clearSelections: clearSelectionsAction,
  selectNextClaim: selectNextClaimAction,
  selectPreviousClaim: selectPreviousClaimAction,
  markFieldAsEdited: markFieldAsEditedAction,
  clearEditedFields: clearEditedFieldsAction,
  markAsSaved: markAsSavedAction,
  discardChanges: discardChangesAction,
  setValidationError: setValidationErrorAction,
  setValidationErrors: setValidationErrorsAction,
  clearValidationError: clearValidationErrorAction,
  clearAllValidationErrors: clearAllValidationErrorsAction,
} = remitSlice.actions;

// Export reducer as default
export default remitSlice.reducer;

// Export state type
export type { RemitSliceState } from './state';

// Export all selectors
export * from './selectors';
